import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import type { SSEEvent, SessionStatus } from '../api/types.js';
import { useApiClient } from './use-api-client.js';
import { lastAgentEventId } from './event-cursor.js';

/** Reads after a turn ends, for what comes after it (the prompt suggestion) */
export const AFTER_TURN_MS: readonly number[] = [3_000, 8_000, 20_000];
/**
 * Reads while the session rests: a turn can start from elsewhere (another browser, a Slack reply, a wait, a started
 * session's report) and end between two reads of its status, which this page would then never show (#1296)
 */
export const IDLE_POLL_MS = 30_000;

export function useSessionHistory(serverId: string, sessionId: string, status: SessionStatus | undefined, isStreaming = false) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const cursorRef = useRef(0);
  const prevStatusRef = useRef<SessionStatus | undefined>(status);
  const afterTurnRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const isActive = status === 'processing' || status === 'waiting_for_input';
  const isResting = status === 'idle' || status === 'active';

  const query = useQuery({
    queryKey: ['session-history', serverId, sessionId],
    queryFn: async () => {
      const sinceId = cursorRef.current;
      const newEvents = await api.sessions.history(serverId, sessionId, sinceId);

      const existing = queryClient.getQueryData<SSEEvent[]>(['session-history', serverId, sessionId]) ?? [];

      const newestAgentId = lastAgentEventId(newEvents);
      if (newestAgentId > cursorRef.current) cursorRef.current = newestAgentId;

      if (existing.length === 0) return newEvents;

      // Merge: keep existing events and append only truly new ones
      const existingIds = new Set(existing.map((e) => e.id));
      const unique = newEvents.filter((e) => !existingIds.has(e.id));
      return unique.length > 0 ? [...existing, ...unique] : existing;
    },
    // When SSE stream is active, stop polling — events arrive via the stream
    refetchInterval: isStreaming ? false : (isActive ? 3_000 : isResting ? IDLE_POLL_MS : false),
    staleTime: isActive ? 1_000 : 60_000,
    // Back on the page or on a tab left in the background, where nothing polls: what happened meanwhile
    refetchOnMount: isActive || isResting ? 'always' : true,
    refetchOnWindowFocus: isActive || isResting ? 'always' : false,
  });

  // Reconcile: when session transitions from active to idle, do a final fetch
  // to capture any events that arrived between the last poll/SSE and the status change.
  // Then a few more: Claude Code sends its proposed next message 1.5 to 2.5 s after the turn ends, when nothing
  // polls or streams any more, so a single read at idle comes before it
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = status;

    const wasActive = prev === 'processing' || prev === 'waiting_for_input' || prev === 'active';
    // A new turn: what comes after the previous one no longer matters, the poll or the stream takes over
    if (status !== 'idle') {
      afterTurnRef.current.forEach(clearTimeout);
      afterTurnRef.current = [];
    }
    if (!wasActive || status !== 'idle') return;
    query.refetch();
    afterTurnRef.current = AFTER_TURN_MS.map((ms) => setTimeout(() => query.refetch(), ms));
  }, [status, query.refetch]);

  useEffect(() => () => afterTurnRef.current.forEach(clearTimeout), []);

  const reset = useCallback(() => {
    cursorRef.current = 0;
    queryClient.removeQueries({ queryKey: ['session-history', serverId, sessionId] });
  }, [queryClient, serverId, sessionId]);

  return { ...query, reset };
}
